import { FolderOpen, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RoleBadge } from '../components/RoleBadge';
import { RoleProtectedAction } from '../components/RoleProtectedAction';
import { FlowsTable } from '../components/tables/FlowsTable';
import { useAuth } from '../contexts/AuthContext';
import { useUIStore } from '../stores/uiStore';
import type { FlowPaginationParams } from '../types/pagination';

/**
 * Main WorkflowList component with performance optimizations
 */
export function WorkflowList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { user, hasRole, canWrite } = useAuth();

	// Zustand store for UI state - use specific selectors to avoid unnecessary re-renders
	const workflowFilters = useUIStore(state => state.workflowFilters);
	const setWorkflowFilters = useUIStore(state => state.setWorkflowFilters);

	// Create stable references for filter values
	const stableSearch = useMemo(() => workflowFilters.search, [workflowFilters.search]);
	const stableFolder = useMemo(
		() => workflowFilters.selectedFolder,
		[workflowFilters.selectedFolder]
	);
	const stableStatus = useMemo(() => workflowFilters.status, [workflowFilters.status]);
	const stableTags = useMemo(
		() => workflowFilters.selectedTags?.join(',') || '',
		[workflowFilters.selectedTags]
	);

	// Memoize setWorkflowFilters to prevent unnecessary re-renders
	const memoizedSetWorkflowFilters = useCallback(setWorkflowFilters, [setWorkflowFilters]);

	// Initialize folder filter from URL parameters (URL takes precedence over store)
	useEffect(() => {
		const folderParam = searchParams.get('folder');

		if (folderParam) {
			// Always set folder filter when URL parameter is present (URL takes precedence)
			memoizedSetWorkflowFilters({
				selectedFolder: folderParam,
				showFilters: true,
			});
		} else {
			// Clear folder filter when no folder parameter is present
			memoizedSetWorkflowFilters({
				selectedFolder: '',
				showFilters: false,
			});
		}
	}, [searchParams.get('folder'), memoizedSetWorkflowFilters]);

	// Convert UI store filters to FlowsTable initial filters (URL params take precedence)
	const initialFilters: Partial<FlowPaginationParams> = useMemo(() => {
		const filters: Partial<FlowPaginationParams> = {};

		// URL parameters take precedence over store state
		const urlFolderParam = searchParams.get('folder');

		if (stableSearch) {
			filters.search = stableSearch;
		}
		if (urlFolderParam) {
			// Use URL parameter directly for immediate filtering
			filters.folderId = urlFolderParam;
		} else if (stableFolder) {
			// Fallback to store state if no URL parameter
			filters.folderId = stableFolder;
		}
		if (stableStatus) {
			filters.status = stableStatus;
		}
		if (stableTags) {
			filters.tags = stableTags.split(',').filter(Boolean);
		}

		return filters;
	}, [stableSearch, stableFolder, stableStatus, stableTags, searchParams.get('folder')]);

	return (
		<div className="px-4 py-8 w-full sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<div className="flex items-center space-x-3">
							<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
								Workflows
							</h1>
							{user && user.roles && user.roles.length > 0 && (
								<RoleBadge role={user.roles[0]} size="sm" showIcon={true} />
							)}
						</div>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							Manage your saved workflows and automations
						</p>
					</div>

					<div className="flex gap-3 items-center">
						<Link
							to="/folders"
							className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm transition-colors dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
							title="Manage folders to organize workflows"
						>
							<FolderOpen className="w-4 h-4" />
							Manage Folders
						</Link>

						{/* Role-protected New Workflow button */}
						<RoleProtectedAction
							requiredRole="WRITE"
							showUpgradePrompt={true}
							action="create new workflows"
						>
							<Link
								to="/editor"
								className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
								title="Create a new workflow"
							>
								<Plus className="w-4 h-4" />
								New Workflow
							</Link>
						</RoleProtectedAction>
					</div>
				</div>
			</div>

			{/* FlowsTable with built-in pagination, search, and filtering */}
			<FlowsTable
				initialFilters={initialFilters}
				showSearch={true}
				showFilters={true}
				className="bg-white rounded-lg border border-gray-200 shadow-sm dark:bg-gray-800 dark:border-gray-700"
			/>
		</div>
	);
}
